import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Loader2, ArrowUpRight } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
    "w-full bg-transparent border-b border-edge focus:border-forest outline-none py-3 text-base font-light text-ink placeholder:text-fern/60 transition-colors duration-300 rounded-none";

const initial = { name: "", company: "", email: "", phone: "", service: "", message: "" };

const serviceOptions = [
    "Office Cleaning",
    "Retail & Food-Service Cleaning",
    "Industrial & Factory Cleaning",
    "Contract Cleaning",
    "Deep Cleaning",
    "Something else",
];

export default function ContactForm() {
    const [form, setForm] = useState(initial);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, {
                name: form.name,
                company: form.company || null,
                email: form.email,
                phone: form.phone || null,
                service: form.service || null,
                message: form.message,
            });
            toast.success("Enquiry sent — we'll be in touch within one working day.");
            setForm(initial);
        } catch {
            toast.error("Something went wrong sending your enquiry. Please try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="contact-section">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="lg:col-span-5"
                >
                    <p className="text-xs font-bold tracking-[0.25em] uppercase text-fern mb-4">Start a conversation</p>
                    <h2 className="font-serif font-medium tracking-tight leading-tight text-3xl sm:text-4xl lg:text-5xl text-forest mb-8 text-balance">
                        Tell us about your space
                    </h2>
                    <p className="text-base font-light leading-relaxed tracking-wide text-fern max-w-md mb-12">
                        Share a few details and we'll come back with a quotation — usually within one
                        working day.
                    </p>
                    <div className="flex flex-col gap-5">
                        <a
                            data-testid="contact-email-link"
                            href="mailto:info@thezamiconcept.com"
                            className="flex items-center gap-3 text-forest hover:text-ink transition-colors duration-300"
                        >
                            <Mail className="w-4 h-4" strokeWidth={1.5} />
                            <span className="text-sm font-medium">info@thezamiconcept.com</span>
                        </a>
                        <a
                            data-testid="contact-phone-link"
                            href="tel:+2348039485843"
                            className="flex items-center gap-3 text-forest hover:text-ink transition-colors duration-300"
                        >
                            <Phone className="w-4 h-4" strokeWidth={1.5} />
                            <span className="text-sm font-medium">+234 803 948 5843</span>
                        </a>
                        <div className="flex items-center gap-3 text-forest" data-testid="contact-location">
                            <MapPin className="w-4 h-4" strokeWidth={1.5} />
                            <span className="text-sm font-medium">Wemabod Estate, Ikeja, Lagos</span>
                        </div>
                        <div className="flex items-center gap-3 text-forest" data-testid="contact-coverage">
                            <MapPin className="w-4 h-4" strokeWidth={1.5} />
                            <span className="text-sm font-medium">Serving Lagos and multiple states across Nigeria</span>
                        </div>
                    </div>
                </motion.div>

                <motion.form
                    data-testid="contact-form"
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
                    className="lg:col-span-7 bg-mist border border-edge rounded-3xl p-8 md:p-12"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8">
                        <div>
                            <label htmlFor="contact-name" className="text-xs font-bold tracking-[0.2em] uppercase text-fern">Name *</label>
                            <input
                                id="contact-name"
                                data-testid="contact-name-input"
                                required
                                value={form.name}
                                onChange={set("name")}
                                placeholder="Jane Appleseed"
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label htmlFor="contact-company" className="text-xs font-bold tracking-[0.2em] uppercase text-fern">Company</label>
                            <input
                                id="contact-company"
                                data-testid="contact-company-input"
                                value={form.company}
                                onChange={set("company")}
                                placeholder="Acme Ltd"
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label htmlFor="contact-email" className="text-xs font-bold tracking-[0.2em] uppercase text-fern">Email *</label>
                            <input
                                id="contact-email"
                                data-testid="contact-email-input"
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                placeholder="jane@acme.com"
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label htmlFor="contact-phone" className="text-xs font-bold tracking-[0.2em] uppercase text-fern">Phone</label>
                            <input
                                id="contact-phone"
                                data-testid="contact-phone-input"
                                type="tel"
                                value={form.phone}
                                onChange={set("phone")}
                                placeholder="+234 800 000 0000"
                                className={inputClass}
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="contact-service" className="text-xs font-bold tracking-[0.2em] uppercase text-fern">Service needed</label>
                            <select
                                id="contact-service"
                                data-testid="contact-service-select"
                                value={form.service}
                                onChange={set("service")}
                                className={`${inputClass} cursor-pointer bg-transparent`}
                            >
                                <option value="">Select a service…</option>
                                {serviceOptions.map((o) => (
                                    <option key={o}>{o}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="contact-message" className="text-xs font-bold tracking-[0.2em] uppercase text-fern">Your space *</label>
                            <textarea
                                id="contact-message"
                                data-testid="contact-message-input"
                                required
                                rows={4}
                                value={form.message}
                                onChange={set("message")}
                                placeholder="Tell us about your facility — size, floors, team count, current arrangement…"
                                className={`${inputClass} resize-none`}
                            />
                        </div>
                    </div>
                    <button
                        data-testid="contact-submit-button"
                        type="submit"
                        disabled={sending}
                        className="group mt-10 rounded-full bg-forest text-background text-xs font-bold tracking-[0.15em] uppercase px-10 py-4 flex items-center gap-2 hover:-translate-y-1 transition-transform duration-300 disabled:opacity-60 disabled:translate-y-0"
                    >
                        {sending ? (
                            <>
                                Sending <Loader2 className="w-4 h-4 animate-spin" />
                            </>
                        ) : (
                            <>
                                Send enquiry
                                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                            </>
                        )}
                    </button>
                </motion.form>
            </div>
        </section>
    );
}
